import _ from 'lodash';
import {List} from 'immutable';
import {
  EDITOR_PAGE_FETCHED,
  PAGE_SWITCHED,
  RAW_SITE_DATA_RECEIVED,
  CREATE_COMPONENT_ACROSS_LOCALES_STARTED,
  CREATE_COMPONENT_ACROSS_LOCALES_PROGRESSED,
  CREATE_COMPONENT_ACROSS_LOCALES_FAILED,
  ROOT_SYMBOL_NODE_ADDED,
  DUPLICATE_COMPONENT_ACROSS_LOCALES_STARTED,
  DUPLICATE_COMPONENT_ACROSS_LOCALES_PROGRESSED,
  DUPLICATE_COMPONENT_ACROSS_LOCALES_FAILED,
  SYMBOL_ROOT_DUPLICATED,
  UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_STARTED,
  UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_PROGRESSED,
  UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_FAILED,
  COMPONENT_INSTANCE_UNLINKED,
  CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_STARTED,
  CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_PROGRESSED,
  CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_FAILED,
  DATA_TYPE_CHANGED,
  SYMBOL_ROOT_FIELDS_CREATED_AND_BOUND,
  LOCALIZATION_CORRUPTION_RECOVERY_STARTED,
  LOCALIZATION_CORRUPTION_RECOVERY_PROGRESSED,
  LOCALIZATION_CORRUPTION_RECOVERY_FAILED,
  LOCALIZATION_CORRUPTION_RECOVERY_FINISHED,
  LOCALIZATION_CORRUPTION_REPORT_FETCH_FINISHED,
  LOCALIZATION_CORRUPTION_REPORT_FETCH_FAILED,
  LOCALIZATION_CORRUPTION_RECOVERY_ENABLED_REMOTELY,
  LOCALIZATION_CORRUPTION_REPORT_UPDATED_REMOTELY,
  CORRUPTION_RECOVERY_PANEL_OPENED,
  CORRUPTION_RECOVERY_PANEL_CLOSED,
  LOST_TRANSLATIONS_FETCH_FINISHED,
  LOST_TRANSLATIONS_FETCH_FAILED,
  LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FINISHED,
  LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FAILED,
} from '@packages/systems/designer/actions';
import {
  LOCALIZATION_SETTINGS_SAVED,
  LOCALIZATION_LOCALES_LOADED,
  LOCALIZATION_DATA_UPDATED_REMOTELY,
  LOCALIZATION_SETTINGS_SAVING,
  LOCALIZATION_SETTINGS_CHANGES_DISCARDED,
  type LocalizationAction,
  LOCALIZATION_SETTINGS_LOCALE_DELETED,
  LOCALIZATION_SETTINGS_LOCALE_SELECTED,
  LOCALIZATION_STAGED_SETTINGS_CHANGED,
  LOCALIZATION_SETTINGS_PRESTAGED_LOCALES_RESET,
  CURRENT_DOM_LOCALIZATION_UPDATED,
  CURRENT_SYMBOLS_LOCALIZATION_UPDATED,
  DOM_LOCALIZATION_NODE_RESET,
  LOCALIZATION_STORAGE_LAYER_MIGRATION_COMPLETED_REMOTELY,
  CURRENT_LOCALE_CHANGED,
  LOCALIZATION_SETTINGS_HREFLANG_PREFIX_INVALID,
  MULTIPLAYER_DATA_CATCHUP_RESPONDED,
  LOCALIZATION_CONTEXTUAL_ACTION_SET,
  LOCALIZED_NODE_SYNCED_WITH_PRIMARY,
  LOCALIZATION_CREATE_LANGUAGE_TARGET_INTENT_TOGGLED,
} from './actions';

import type {
  Locale,
  LocalizationState,
} from '@packages/systems/localization-types';

import LocaleUtils, {makeLocale, makeStagedState} from '../utils/Locale';
import {isValidLocaleTag} from '../utils/fixtures';
import {Tree} from '@packages/systems/tree';
import {telemetry} from '@packages/systems/designer-telemetry';
import {
  PANE_UPDATED,
  isPanelClosing,
  isPanelOpening,
} from '@packages/systems/panels';
import {
  getChildren,
  getComponentRootElement,
  isPathContainingInstances,
} from '@packages/utilities/platform';
import {isRichText} from '@packages/utilities/elementTypeUtils';
import {getPluginsFromState} from '@packages/utilities/platform-selectors';
import {Expressions, Plugins, Component} from '@packages/systems/wfdl';
import {QualifiedMap, qualify} from '@packages/systems/wfdl-Qualified';
import {
  selectPrimaryDom,
  selectLocalizedDom,
  selectLocalizedSymbols,
  selectLocaleById,
  selectAllLocales,
} from '../utils/selectors';
import {collectLocalizedDescendantsIds} from '../utils/collectLocalizedDescendantsIds';
import {force, deferUntilIdle, defer} from '@packages/utilities/lazy';
import {NEW_SIDEBAR_CHILDREN} from './newSidebarChildren';

export const defaultState: LocalizationState = {
  currentLocale: undefined,
  locales: [],
  primaryLocale: undefined,
  staged: makeStagedState(),
  dom: {
    primary: defer(() => QualifiedMap.empty()),
    localized: {},
  },
  symbols: {
    localized: {},
  },
  publishedSiteBrowserDetection: false,
  enableHreflangLinks: true,
  enableCorruptionRecovery: false,
  contextualAction: null,
};

// cmsId is left out: the server assigns it during the same save and
// broadcasts both before and after, so echoes arrive with or without it.
const toComparableLocales = (primary: Locale, locales: Array<Locale>) =>
  List(
    [primary, ...locales].map((locale) => makeLocale(locale).remove('cmsId'))
  );

// The saving client also receives its own save as a remote update. When it
// matches what we already hold, nothing changed for the settings panel.
const isEchoOfPersistedLocalization = (
  state: LocalizationState,
  incoming: Pick<
    LocalizationState,
    | 'locales'
    | 'publishedSiteBrowserDetection'
    | 'enableHreflangLinks'
    | 'hrefPrefixSameAsCanonicalUrl'
    | 'hrefPrefixForHreflangLinks'
  > & {primaryLocale: Locale}
): boolean =>
  state.primaryLocale !== undefined &&
  incoming.publishedSiteBrowserDetection ===
    state.publishedSiteBrowserDetection &&
  incoming.enableHreflangLinks === state.enableHreflangLinks &&
  incoming.hrefPrefixSameAsCanonicalUrl ===
    state.hrefPrefixSameAsCanonicalUrl &&
  incoming.hrefPrefixForHreflangLinks === state.hrefPrefixForHreflangLinks &&
  toComparableLocales(incoming.primaryLocale, incoming.locales).equals(
    toComparableLocales(state.primaryLocale, state.locales)
  );

const reducer = (
  state: LocalizationState,
  action: LocalizationAction,
  enableExternalizedBindings = false
): LocalizationState => {
  switch (action.type) {
    case EDITOR_PAGE_FETCHED: {
      const {
        localization: {
          primary: primaryLocale = state.primaryLocale,
          locales = state.locales,
        } = {},
      } = action.pageData;

      if (primaryLocale) {
        primaryLocale.primary = true;
      }
      locales.forEach((secondaryLocale) => {
        secondaryLocale.primary = false;
      });

      const currentLocale =
        locales.find(
          (locale) =>
            LocaleUtils.getSubdirectory(locale) ===
            action.maybeLocaleSubdirectory
        ) ?? primaryLocale;

      return {
        ...state,
        locales,
        primaryLocale,
        currentLocale,
      };
    }
    case RAW_SITE_DATA_RECEIVED: {
      const {
        localization: {
          primary: primaryLocale,
          locales = [],
          publishedSiteBrowserDetection = false,
          enableHreflangLinks = true,
          hrefPrefixForHreflangLinks,
          enableCorruptionRecovery,
        } = {},
        domNodes = [],
        localizedDom = {},
        localizedSymbols = {},
        symbols,
        localeId,
      } = action.siteData;
      let {hrefPrefixSameAsCanonicalUrl = true} = action.siteData.localization;

      if (
        enableHreflangLinks &&
        hrefPrefixSameAsCanonicalUrl === false &&
        !hrefPrefixForHreflangLinks
      ) {
        // If the user has enabled hreflang links but not provided a prefix,
        // we need to default to using the canonical (or published) URL.
        hrefPrefixSameAsCanonicalUrl = true;
      }

      if (primaryLocale) {
        primaryLocale.primary = true;
      }
      locales.forEach((secondaryLocale) => {
        secondaryLocale.primary = false;
      });

      const plugins = getPluginsFromState({
        DesignerStore: action.state.DesignerStore,
      });

      // Primary dom is stored as a lazy computation so we can defer it as late as possible until the UI needs it.
      const primaryDom = deferUntilIdle(() =>
        Tree.fromJSONNodesWithPluginComponents(
          domNodes,
          symbols,
          plugins
        ).asComponentMap()
      );
      const currentLocale =
        locales.find((locale) => locale._id === localeId) ??
        primaryLocale ??
        undefined;

      const rawEnableExternalizedBindings =
        action.siteData.localization?.enableExternalizedBindings ?? false;

      return {
        ...state,
        currentLocale,
        locales,
        primaryLocale: primaryLocale ?? defaultState.primaryLocale,
        dom: {
          primary: primaryDom,
          localized: rawEnableExternalizedBindings ? {} : localizedDom,
        },
        symbols: {
          localized: rawEnableExternalizedBindings ? {} : localizedSymbols,
        },
        publishedSiteBrowserDetection,
        enableHreflangLinks,
        hrefPrefixForHreflangLinks,
        hrefPrefixSameAsCanonicalUrl,
        enableCorruptionRecovery,
      };
    }

    case LOCALIZATION_LOCALES_LOADED:
    case LOCALIZATION_DATA_UPDATED_REMOTELY: {
      const payload = action.payload;
      const primaryLocale = payload.primary ?? undefined;
      const locales = payload.locales ?? [];

      // The `primary` field is optional and not always set even for the primary locale.
      //   At the same time, in some places in our codebase we assume the primary locale
      //   always has `primary: true`. Therefore we decided to patch incoming data
      //   like here to make that assumption correct.
      if (primaryLocale) {
        primaryLocale.primary = true;
      }
      locales.forEach((secondaryLocale) => {
        secondaryLocale.primary = false;
      });

      const prevCurrentLocale: Locale | undefined = state.currentLocale;
      const nextCurrentLocale: Locale | undefined = [
        primaryLocale,
        ...locales,
      ].find((l) => l != null && l._id === prevCurrentLocale?._id);
      const currentLocale =
        nextCurrentLocale ?? prevCurrentLocale ?? primaryLocale;

      const finalPrimaryLocale = primaryLocale ?? state.primaryLocale;

      // There is no way this can happen since if we are here then
      // there is a primary locale but typescript doesn't know that.
      if (!finalPrimaryLocale) {
        return {
          ...state,
          primaryLocale,
          locales,
          currentLocale,
        };
      }

      const stagedPrimaryLocale = makeLocale(finalPrimaryLocale);
      const finalLocales = List([
        stagedPrimaryLocale,
        ...locales.map(makeLocale),
      ]);

      const finalPublishedSiteBrowserDetection =
        payload.publishedSiteBrowserDetection ?? false;
      const finalEnableHreflangLinks = payload.enableHreflangLinks ?? false;
      const finalHrefPrefixSameAsCanonicalUrl =
        payload.hrefPrefixSameAsCanonicalUrl ?? true;
      const finalHrefPrefixForHreflangLinks =
        payload.hrefPrefixForHreflangLinks ?? state.hrefPrefixForHreflangLinks;

      // Resetting staged on our own save's echo would close the locale row the
      // user just opened in the settings panel and drop their pending edits.
      if (
        action.type === LOCALIZATION_DATA_UPDATED_REMOTELY &&
        !state.staged.preStagedLocales.isEmpty() &&
        isEchoOfPersistedLocalization(state, {
          primaryLocale: finalPrimaryLocale,
          locales,
          publishedSiteBrowserDetection: finalPublishedSiteBrowserDetection,
          enableHreflangLinks: finalEnableHreflangLinks,
          hrefPrefixSameAsCanonicalUrl: finalHrefPrefixSameAsCanonicalUrl,
          hrefPrefixForHreflangLinks: finalHrefPrefixForHreflangLinks,
        })
      ) {
        return {...state, primaryLocale, locales, currentLocale};
      }

      return {
        ...state,
        primaryLocale,
        locales,
        currentLocale,
        publishedSiteBrowserDetection: finalPublishedSiteBrowserDetection,
        enableHreflangLinks: finalEnableHreflangLinks,
        hrefPrefixSameAsCanonicalUrl: finalHrefPrefixSameAsCanonicalUrl,
        hrefPrefixForHreflangLinks: finalHrefPrefixForHreflangLinks,
        staged: makeStagedState({
          locales: finalLocales,
          preStagedLocales: finalLocales,
          primaryLocale: stagedPrimaryLocale,
          selectedLocaleIndex: undefined,
          publishedSiteBrowserDetection: finalPublishedSiteBrowserDetection,
          enableHreflangLinks: finalEnableHreflangLinks,
          hrefPrefixSameAsCanonicalUrl: finalHrefPrefixSameAsCanonicalUrl,
          hrefPrefixForHreflangLinks: finalHrefPrefixForHreflangLinks,
        }),
      };
    }

    case LOCALIZATION_STORAGE_LAYER_MIGRATION_COMPLETED_REMOTELY:
    case LOCALIZATION_SETTINGS_SAVED: {
      const {
        primary: primaryLocale,
        locales = [],
        publishedSiteBrowserDetection = false,
        enableHreflangLinks = true,
        hrefPrefixForHreflangLinks,
        hrefPrefixSameAsCanonicalUrl,
      } = action.payload ?? {};

      if (primaryLocale) {
        primaryLocale.primary = true;
      }
      locales.forEach((secondaryLocale) => {
        secondaryLocale.primary = false;
      });

      const allNewLocales = [primaryLocale, ...locales];
      const {dom, currentLocale} = state;
      const nextLocale =
        (currentLocale
          ? allNewLocales.find((loc) => loc?._id === currentLocale._id)
          : primaryLocale) ?? currentLocale;

      return {
        ...state,
        currentLocale: nextLocale,
        locales,
        primaryLocale: primaryLocale ?? defaultState.primaryLocale,
        dom,
        publishedSiteBrowserDetection,
        enableHreflangLinks,
        hrefPrefixForHreflangLinks,
        hrefPrefixSameAsCanonicalUrl,
        staged: makeStagedState(),
      };
    }

    case CURRENT_DOM_LOCALIZATION_UPDATED: {
      const localizedDom = action.payload;

      return {
        ...state,
        dom: {
          ...state.dom,
          localized: localizedDom,
        },
      };
    }

    case CURRENT_SYMBOLS_LOCALIZATION_UPDATED: {
      const localizedSymbols = action.payload;

      return {
        ...state,
        symbols: {
          ...state.symbols,
          localized: localizedSymbols || {},
        },
      };
    }

    case DOM_LOCALIZATION_NODE_RESET: {
      const {selectedElement, selectedNativeIdPath} = action.payload;

      const elementId = Expressions.getElementId(selectedElement);

      const userComponentType = qualify(
        Plugins.name('__SitePlugin'),
        Component.name(elementId)
      );

      const primaryDom = force(selectPrimaryDom(state));

      // If it is a User Component
      if (primaryDom.has(userComponentType)) {
        const rootComponentExpression = getComponentRootElement(
          primaryDom,
          userComponentType
        );

        if (rootComponentExpression) {
          const localizedSymbols = selectLocalizedSymbols(state);

          const userComponentNodesToOmit = collectLocalizedDescendantsIds(
            rootComponentExpression,
            localizedSymbols
          );

          const localized = _.omit(localizedSymbols, userComponentNodesToOmit);

          return {...state, symbols: {...state.symbols, localized}};
        }
      }

      const localizedDom = selectLocalizedDom(state);

      if (isRichText(selectedElement)) {
        const richTextNodesToOmit = collectLocalizedDescendantsIds(
          selectedElement,
          localizedDom
        );

        const localized = _.omit(localizedDom, richTextNodesToOmit);

        return {...state, dom: {...state.dom, localized}};
      }

      const children = getChildren(selectedElement) ?? [];

      // Is a node inside of a User Component
      if (isPathContainingInstances(selectedNativeIdPath)) {
        const localized = _.omit(selectLocalizedSymbols(state), [
          elementId,
          ...children.map((child) => child.id),
        ]);

        return {...state, symbols: {...state.symbols, localized}};
      }

      // Otherwise if it is just a node in the dom
      const localized = _.omit(localizedDom, [
        elementId,
        ...children.map((child) => child.id),
      ]);

      return {...state, dom: {...state.dom, localized}};
    }

    case LOCALIZATION_SETTINGS_LOCALE_DELETED: {
      const {staged} = state;
      const selectedLocaleIndex = staged.get('selectedLocaleIndex');

      if (selectedLocaleIndex === undefined) {
        return state;
      }

      const newLocales = staged.locales.delete(selectedLocaleIndex) ?? List([]);

      // When a locale is deleted, also remove its tag from the intent set so
      // handleSave doesn't attempt to create a target for a now-absent locale.
      const deletedLocale = staged.locales.get(selectedLocaleIndex);
      const nextIntentTags = deletedLocale
        ? staged.get('createLanguageTargetTags').delete(deletedLocale.tag)
        : staged.get('createLanguageTargetTags');

      return {
        ...state,
        staged: staged
          ?.set('locales', newLocales)
          .set('preStagedLocales', newLocales)
          .set('selectedLocaleIndex', undefined)
          .set('createLanguageTargetTags', nextIntentTags),
      };
    }

    case LOCALIZATION_CREATE_LANGUAGE_TARGET_INTENT_TOGGLED: {
      const {tag, checked} = action.payload;
      const {staged} = state;
      const currentTags = staged.get('createLanguageTargetTags');
      const nextTags = checked ? currentTags.add(tag) : currentTags.delete(tag);
      return {
        ...state,
        staged: staged.set('createLanguageTargetTags', nextTags),
      };
    }

    case LOCALIZATION_SETTINGS_LOCALE_SELECTED: {
      const index = action.payload;
      let {staged} = state;

      staged = staged.set('selectedLocaleIndex', index);

      if (index === undefined) {
        staged = staged.set('primaryLocale', staged.preStagedLocales.first());
      }

      return {
        ...state,
        staged,
      };
    }

    case LOCALIZATION_SETTINGS_PRESTAGED_LOCALES_RESET: {
      const {staged} = state;

      return {
        ...state,
        staged: staged.set('preStagedLocales', staged.locales),
      };
    }

    case LOCALIZATION_STAGED_SETTINGS_CHANGED: {
      const {keyPath, value} = action.payload;
      const {staged} = state;

      let newStaged = staged.setIn(keyPath, value);

      if (keyPath[keyPath.length - 1] === 'tag') {
        // Editing a locale's language/country changes its tag. If the previous
        // tag had a pending create-target intent, drop it — the locale is now a
        // different tag, so leaving the old tag in the set would create an
        // unnecessary/orphan language target on save. The new tag's intent is
        // (re)committed on inline-save via LOCALIZATION_CREATE_LANGUAGE_TARGET_INTENT_TOGGLED.
        const previousTag = staged.getIn(keyPath);
        if (
          typeof previousTag === 'string' &&
          previousTag !== value &&
          newStaged.get('createLanguageTargetTags').has(previousTag)
        ) {
          newStaged = newStaged.set(
            'createLanguageTargetTags',
            newStaged.get('createLanguageTargetTags').delete(previousTag)
          );
        }

        const correspondingSubdirectoryPath = keyPath
          .slice(0, -1)
          .concat('subdirectory');

        const correspondingSubdirectoryValue =
          staged.getIn(correspondingSubdirectoryPath) ?? '';

        // As long as the current subdirectory value isn't a custom value
        // (ie, not within our pre-defined locale values) then
        // we need to set the subdirectory value.
        if (
          !correspondingSubdirectoryValue ||
          (typeof correspondingSubdirectoryValue === 'string' &&
            isValidLocaleTag(correspondingSubdirectoryValue))
        ) {
          newStaged = newStaged.setIn(correspondingSubdirectoryPath, value);
        }

        const correspondingDisplayNamePath = keyPath
          .slice(0, -1)
          .concat('displayName');
        const correspondingDisplayName =
          typeof value === 'string' ? LocaleUtils.getTagDescription(value) : '';

        if (correspondingDisplayName) {
          // reset the display name to the default for the new locale
          newStaged = newStaged.setIn(
            correspondingDisplayNamePath,
            correspondingDisplayName
          );
        }
      }

      return {
        ...state,
        staged: newStaged,
      };
    }

    case LOCALIZATION_SETTINGS_SAVING: {
      const isSaving = action.payload;
      const {staged: newStaged} = state;

      return {
        ...state,
        staged: newStaged.set('isSaving', isSaving),
      };
    }

    case LOCALIZATION_SETTINGS_CHANGES_DISCARDED: {
      const {staged} = state;

      // The staged locales are the values before any current edits, so we can just reset back to that.
      // Rebuild the full staged record from the persisted top-level state so that non-locale fields
      // (URL routing, SEO, auto-translate) are also reverted — previously only preStagedLocales and
      // primaryLocale were reset, leaving those fields stale until a Designer reload.
      const locales = staged.get('locales');

      return {
        ...state,
        staged: makeStagedState({
          locales,
          preStagedLocales: locales,
          primaryLocale: locales.first(),
          selectedLocaleIndex: undefined,
          publishedSiteBrowserDetection: state.publishedSiteBrowserDetection,
          enableHreflangLinks: state.enableHreflangLinks,
          hrefPrefixForHreflangLinks: state.hrefPrefixForHreflangLinks,
          hrefPrefixSameAsCanonicalUrl: state.hrefPrefixSameAsCanonicalUrl,
        }),
      };
    }

    case LOCALIZATION_SETTINGS_HREFLANG_PREFIX_INVALID: {
      const {staged: newStaged} = state;

      return {
        ...state,
        staged: newStaged.set('isSaving', false),
      };
    }

    case LOCALIZATION_CONTEXTUAL_ACTION_SET: {
      return {
        ...state,
        contextualAction: action.payload,
      };
    }

    case CURRENT_LOCALE_CHANGED: {
      const {localeId} = action.payload;
      const foundLocale = selectLocaleById(selectAllLocales(state))(
        String(localeId)
      );

      if (!foundLocale) {
        return state;
      }

      return {
        ...state,
        currentLocale: foundLocale,
        // The `loading` slice is targeted at a specific node sync; a locale
        // change means any in-flight sync is no longer relevant. Without
        // this reset, an aborted sync would leave elementSyncState stuck on
        // 'loading' for the rest of the session, suppressing the
        // ComponentInstancePropertiesPanel.
        elementSyncInProgressCount: 0,
        elementSyncState:
          state.elementSyncState === 'loading'
            ? ('idle' as const)
            : state.elementSyncState,
      };
    }

    case PAGE_SWITCHED: {
      const {localizedDom, jsonNodes, localizedSymbols, symbols, localeId} =
        action;

      const foundLocale = selectLocaleById(selectAllLocales(state))(
        String(localeId)
      );

      const plugins = getPluginsFromState({
        DesignerStore: action.state.DesignerStore,
      });

      // Primary dom is stored as a lazy computation so we can defer it as late as possible until the UI needs it.
      const primary = deferUntilIdle(() => {
        const primaryDomPageSwitchStart = Date.now();
        const symbolsProvided = !_.isNil(symbols);

        const tree = Tree.create(force(state.dom.primary));
        const componentMap = (
          symbolsProvided
            ? tree.updatePageWithJSONNodesAndSymbols(
                jsonNodes,
                symbols,
                plugins
              )
            : tree.updatePageWithJSONNodes(jsonNodes, plugins)
        ).asComponentMap();
        const duration = Date.now() - primaryDomPageSwitchStart;
        const resolvedSymbolCount = symbolsProvided ? symbols.length : 0;

        telemetry.sendEvent('datadog::localizationPrimaryDomPageSwitch', {
          value: duration,
          symbolsProvided,
          nodeCount: jsonNodes.length,
          providedSymbolCount: symbols?.length ?? 0,
          resolvedSymbolCount,
          durationToResolvedSymbolCountRatio:
            resolvedSymbolCount > 0 ? duration / resolvedSymbolCount : 0,
        });

        return componentMap;
      });

      return {
        ...state,
        currentLocale: foundLocale || state.currentLocale,
        dom: {
          primary,
          localized: enableExternalizedBindings ? {} : localizedDom || {},
        },
        symbols: {
          localized: enableExternalizedBindings ? {} : localizedSymbols || {},
        },
      };
    }

    case PANE_UPDATED: {
      const isLegacyOpening = isPanelOpening(action, 'localization');
      const isNewSidebarOpening = isPanelOpening(action, 'localization-panel');
      const isAnyNewSidebarChildOpening = NEW_SIDEBAR_CHILDREN.some((name) =>
        isPanelOpening(action, name)
      );
      const isAnyNewSidebarChildClosing = NEW_SIDEBAR_CHILDREN.some((name) =>
        isPanelClosing(action, name)
      );
      const isAnyNewSidebarChildTransitioning =
        isAnyNewSidebarChildOpening || isAnyNewSidebarChildClosing;

      // Hydrate staged from top-level state when the user ENTERS the
      // localization UI (either the legacy panel opens, or the new sidebar
      // parent opens). Also hydrate when a new-sidebar child opens AND
      // staged is currently in the default empty state — this covers the
      // post-save path: LOCALIZATION_SETTINGS_SAVED resets staged to the
      // empty default, and the next child open (the sidebar parent stays
      // open across save) must re-hydrate from the saved top-level state,
      // otherwise the child renders blank (`staged.locales.size <= 1`
      // returns null in URL routing / SEO / Glossary).
      //
      // Do NOT re-hydrate on sibling navigation when staged is non-empty —
      // that would overwrite the user's pending edits across child panels.
      const stagedIsDefault = state.staged.equals(makeStagedState());

      if (
        isLegacyOpening ||
        isNewSidebarOpening ||
        (isAnyNewSidebarChildOpening && stagedIsDefault)
      ) {
        const {locales, primaryLocale, staged} = state;

        let stagedPrimaryLocale = makeLocale({...primaryLocale, primary: true});

        let selectedLocaleIndex = staged.selectedLocaleIndex;

        if (!primaryLocale) {
          // Automatically expand the primary locale row if there's no primary locale set up yet
          selectedLocaleIndex = 0;

          stagedPrimaryLocale = stagedPrimaryLocale
            // We want the primary locale to always redirect to the subdirectory
            .set('redirect', true);
        }

        const preStagedLocales = List([
          stagedPrimaryLocale,
          ...locales.map(makeLocale),
        ]);

        return {
          ...state,
          staged: makeStagedState({
            locales: preStagedLocales,
            preStagedLocales,
            primaryLocale: stagedPrimaryLocale,
            selectedLocaleIndex,
            publishedSiteBrowserDetection: state.publishedSiteBrowserDetection,
            enableHreflangLinks: state.enableHreflangLinks,
            hrefPrefixForHreflangLinks: state.hrefPrefixForHreflangLinks,
            hrefPrefixSameAsCanonicalUrl: state.hrefPrefixSameAsCanonicalUrl,
          }),
        };
      }

      // Internal navigation between new-sidebar siblings — preserve staged
      // so edits persist across Locales ↔ URL Routing ↔ SEO ↔ Glossary.
      if (isAnyNewSidebarChildTransitioning) {
        return state;
      }

      // Avoid wasteful update if staged state already matches default
      const defaultStagedState = makeStagedState();
      if (state.staged.equals(defaultStagedState)) {
        return state;
      }

      return {
        ...state,
        staged: defaultStagedState,
      };
    }

    case CREATE_COMPONENT_ACROSS_LOCALES_STARTED:
      return {
        ...state,
        createComponentState: {
          status: 'IN_PROGRESS',
          pendingPayload: action.payload,
          progress: 0,
        },
      };

    case CREATE_COMPONENT_ACROSS_LOCALES_PROGRESSED:
      if (state.createComponentState?.status !== 'IN_PROGRESS') {
        return {...state, createComponentState: null};
      }

      return {
        ...state,
        createComponentState: {
          ...state.createComponentState,
          progress: action.payload.progress,
        },
      };

    case CREATE_COMPONENT_ACROSS_LOCALES_FAILED:
    case ROOT_SYMBOL_NODE_ADDED:
      return {...state, createComponentState: null};

    case DUPLICATE_COMPONENT_ACROSS_LOCALES_STARTED:
      return {
        ...state,
        duplicateComponentState: {
          status: 'IN_PROGRESS',
          pendingPayload: action.payload,
          progress: 0,
        },
      };

    case DUPLICATE_COMPONENT_ACROSS_LOCALES_PROGRESSED:
      if (state.duplicateComponentState?.status !== 'IN_PROGRESS') {
        return {...state, duplicateComponentState: null};
      }

      return {
        ...state,
        duplicateComponentState: {
          ...state.duplicateComponentState,
          progress: action.payload.progress,
        },
      };

    case DUPLICATE_COMPONENT_ACROSS_LOCALES_FAILED:
    case SYMBOL_ROOT_DUPLICATED:
      return {...state, duplicateComponentState: null};

    case UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_STARTED:
      return {
        ...state,
        unlinkState: {
          status: 'IN_PROGRESS',
          pendingPayload: action.payload,
          progress: 0,
        },
      };

    case UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_PROGRESSED:
      if (state.unlinkState?.status !== 'IN_PROGRESS') {
        return {...state, unlinkState: null};
      }

      return {
        ...state,
        unlinkState: {
          ...state.unlinkState,
          progress: action.payload.progress,
        },
      };

    case UNLINK_COMPONENT_INSTANCE_ACROSS_LOCALES_FAILED:
    case COMPONENT_INSTANCE_UNLINKED:
      return {...state, unlinkState: null};

    case CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_STARTED:
      return {
        ...state,
        createComponentPropertyState: {
          status: 'IN_PROGRESS',
          pendingPayload: action.payload,
          progress: 0,
        },
      };

    case LOCALIZATION_CORRUPTION_RECOVERY_STARTED:
      return {
        ...state,
        corruptionRecoveryState: {
          status: 'IN_PROGRESS',
          progress: 0,
        },
      };

    case CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_PROGRESSED:
      if (state.createComponentPropertyState?.status !== 'IN_PROGRESS') {
        return {...state, createComponentPropertyState: null};
      }

      return {
        ...state,
        createComponentPropertyState: {
          ...state.createComponentPropertyState,
          progress: action.payload.progress,
        },
      };

    case LOCALIZATION_CORRUPTION_RECOVERY_PROGRESSED:
      if (state.corruptionRecoveryState?.status !== 'IN_PROGRESS') {
        return {...state, corruptionRecoveryState: null};
      }

      return {
        ...state,
        corruptionRecoveryState: {
          ...state.corruptionRecoveryState,
          progress: action.payload.progress,
        },
      };

    case LOCALIZATION_CORRUPTION_RECOVERY_FAILED:
    case LOCALIZATION_CORRUPTION_RECOVERY_FINISHED:
      return {...state, corruptionRecoveryState: null};

    case CREATE_COMPONENT_PROPERTY_ACROSS_LOCALES_FAILED:
    case DATA_TYPE_CHANGED:
    case SYMBOL_ROOT_FIELDS_CREATED_AND_BOUND:
    case MULTIPLAYER_DATA_CATCHUP_RESPONDED:
      // Avoid wasteful update if createComponentPropertyState is already null
      if (state.createComponentPropertyState === null) {
        return state;
      }
      return {...state, createComponentPropertyState: null};

    case LOCALIZED_NODE_SYNCED_WITH_PRIMARY: {
      const inProgressCount = state.elementSyncInProgressCount ?? 0;
      return {
        ...state,
        elementSyncInProgressCount: inProgressCount + 1,
        elementSyncState: 'loading' as const,
      };
    }

    case LOCALIZATION_CORRUPTION_REPORT_FETCH_FINISHED: {
      const {report, corruptionRecoveryEnabled} = action.payload.response;
      // Only honor terminal sync-state transitions when a sync was in progress.
      // This action is also fired by designer load and the recovery flows,
      // which have nothing to do with elementSyncState; without this guard,
      // a transient fetch during load would silently clobber the slice.

      const inProgressCount = state.elementSyncInProgressCount ?? 0;
      const hadSyncInProgress = inProgressCount > 0;
      const nextInProgressCount = hadSyncInProgress
        ? inProgressCount - 1
        : inProgressCount;
      const nextElementSyncState = hadSyncInProgress
        ? nextInProgressCount === 0
          ? ('idle' as const)
          : ('loading' as const)
        : state.elementSyncState;

      if (!report)
        return {
          ..._.omit(state, 'corruptionReport'),
          elementSyncInProgressCount: nextInProgressCount,
          elementSyncState: nextElementSyncState,
        };

      return {
        ...state,
        corruptionReport: report,
        enableCorruptionRecovery:
          corruptionRecoveryEnabled || state.enableCorruptionRecovery,
        elementSyncInProgressCount: nextInProgressCount,
        elementSyncState: nextElementSyncState,
      };
    }

    case LOCALIZATION_CORRUPTION_REPORT_FETCH_FAILED: {
      // See LOCALIZATION_CORRUPTION_REPORT_FETCH_FINISHED — only transition
      // when a sync was actually in progress.
      const inProgressCount = state.elementSyncInProgressCount ?? 0;
      if (inProgressCount === 0) {
        return state;
      }

      const nextInProgressCount = inProgressCount - 1;

      return {
        ...state,
        elementSyncInProgressCount: nextInProgressCount,
        elementSyncState: nextInProgressCount === 0 ? 'error' : 'loading',
      };
    }

    case LOCALIZATION_CORRUPTION_RECOVERY_ENABLED_REMOTELY: {
      return {
        ...state,
        enableCorruptionRecovery: true,
      };
    }

    case LOCALIZATION_CORRUPTION_REPORT_UPDATED_REMOTELY: {
      const {report} = action.payload;
      return {
        ...state,
        corruptionReport: report,
      };
    }

    case CORRUPTION_RECOVERY_PANEL_OPENED: {
      const {auditResult} = action.payload;
      const {corruptionType, localeId, element} = auditResult.data;
      // Reopening for the same (node, corruptionType, locale) is a no-op for
      // lostTranslations — preserve the records so the panel doesn't flicker
      // through a loading skeleton on a no-op reopen. RequestStore still
      // re-fires the GET in the background, so a prior `error: true` would
      // otherwise show a stale failure with no indication that a retry is
      // in flight; clear the error and re-enter loading in that case.
      const isSameTarget =
        state.lostTranslations?.nodeId === element.id &&
        state.lostTranslations?.corruptionType === corruptionType &&
        state.lostTranslations?.localeId === localeId;
      return {
        ...state,
        corruptionRecoveryPanel: {
          isOpen: true,
          auditResult,
        },
        lostTranslations: isSameTarget
          ? {
              ...state.lostTranslations!,
              error: false,
              loading: state.lostTranslations!.error
                ? true
                : state.lostTranslations!.loading,
            }
          : {
              records: [],
              nodeId: element.id,
              corruptionType,
              localeId,
              // INVALID_CLASSES corruptions don't trigger a fetch so don't show loading
              loading: corruptionType !== 'INVALID_CLASSES',
              error: false,
            },
      };
    }

    case CORRUPTION_RECOVERY_PANEL_CLOSED: {
      return {
        ...state,
        corruptionRecoveryPanel: {
          isOpen: false,
          auditResult: null,
        },
        lostTranslations: null,
        // Closing the recovery panel implies the user is moving on from the
        // sync attempt that drove this flow; recover elementSyncState so an
        // unmatched LOCALIZED_NODE_SYNCED_WITH_PRIMARY can't strand the
        // ComponentInstancePropertiesPanel for the rest of the session.
        elementSyncInProgressCount: 0,
        elementSyncState:
          state.elementSyncState === 'loading'
            ? ('idle' as const)
            : state.elementSyncState,
      };
    }

    case LOST_TRANSLATIONS_FETCH_FINISHED: {
      if (!state.lostTranslations) return state;
      const {records, nodeId, corruptionType, localeId} =
        action.payload.response;
      // Ignore stale responses: if the panel has switched to a different node
      // while this request was in flight, the identifiers won't match the
      // currently-targeted state and we drop the records.
      if (
        state.lostTranslations.nodeId !== nodeId ||
        state.lostTranslations.corruptionType !== corruptionType ||
        state.lostTranslations.localeId !== localeId
      ) {
        return state;
      }
      return {
        ...state,
        lostTranslations: {
          ...state.lostTranslations,
          records,
          loading: false,
        },
      };
    }

    case LOST_TRANSLATIONS_FETCH_FAILED: {
      if (!state.lostTranslations) return state;
      return {
        ...state,
        lostTranslations: {
          ...state.lostTranslations,
          loading: false,
          error: true,
        },
      };
    }

    case LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FINISHED: {
      const status = action.payload.response;
      // Avoid a wasteful update when availability hasn't changed.
      if (
        state.lostTranslationsReport?.available === status.available &&
        state.lostTranslationsReport?.generatedOn === status.generatedOn &&
        state.lostTranslationsReport?.rowCount === status.rowCount &&
        state.lostTranslationsReport?.partial === status.partial
      ) {
        return state;
      }
      return {...state, lostTranslationsReport: status};
    }

    case LOST_TRANSLATIONS_REPORT_STATUS_FETCH_FAILED: {
      // Keep whatever we knew before — a failed availability check must not
      // hide an already-revealed download affordance.
      return state;
    }
  }
  return state;
};

export default reducer;
